import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X, Instagram, Facebook } from "lucide-react";
import { NAV_LINKS } from "../../data/content";

export const Wordmark = ({ onClick }) => (
  <Link
    to="/"
    onClick={onClick}
    data-testid="brand-logo-link"
    className="group flex items-center gap-3"
    aria-label="Kumite Games home"
  >
    <span className="clip-notch-sm grid h-9 w-9 place-items-center bg-kred font-display text-xl text-white transition-colors duration-300 group-hover:bg-red-500">
      K
    </span>
    <span className="font-display text-2xl tracking-[0.14em] text-bone">
      KUMITE <span className="text-gold">GAMES</span>
    </span>
  </Link>
);

export const Navbar = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [location.pathname]);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => (document.body.style.overflow = "");
  }, [open]);

  return (
    <>
      <header
        data-testid="main-navbar"
        className={`fixed top-0 z-50 w-full border-b backdrop-blur-xl transition-all duration-500 ${
          scrolled ? "border-white/10 bg-black/85 py-3" : "border-transparent bg-black/40 py-5"
        }`}
      >
        <div className="mx-auto flex max-w-[1400px] items-center justify-between px-5 sm:px-8">
          <Wordmark />
          <nav className="hidden items-center gap-5 xl:flex" aria-label="Primary">
            {NAV_LINKS.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={({ isActive }) =>
                  `font-mono text-[10px] font-bold uppercase tracking-[0.2em] transition-colors duration-300 ${
                    isActive ? "text-kred" : "text-slate-400 hover:text-bone"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
          </nav>
          <div className="flex items-center gap-3">
            <div className="hidden items-center gap-1 xl:flex">
              <a
                href="https://www.instagram.com/kumitegames/"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Kumite Games on Instagram"
                data-testid="nav-social-instagram"
                className="grid h-9 w-9 place-items-center text-slate-400 transition-colors duration-300 hover:text-gold"
              >
                <Instagram className="h-4 w-4" />
              </a>
              <a
                href="https://www.facebook.com/kumitegamesnz/"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Kumite Games on Facebook"
                data-testid="nav-social-facebook"
                className="grid h-9 w-9 place-items-center text-slate-400 transition-colors duration-300 hover:text-gold"
              >
                <Facebook className="h-4 w-4" />
              </a>
            </div>
            <Link
              to="/register"
              data-testid="nav-register-button"
              className="clip-notch-sm hidden bg-kred px-6 py-3 font-mono text-[11px] font-bold uppercase tracking-[0.25em] text-white transition-all duration-300 hover:-translate-y-0.5 hover:bg-red-500 hover:shadow-[0_10px_30px_-8px_rgba(220,38,38,0.6)] sm:block"
            >
              Register
            </Link>
            <button
              data-testid="mobile-menu-toggle"
              onClick={() => setOpen(!open)}
              aria-label={open ? "Close menu" : "Open menu"}
              className="grid h-11 w-11 place-items-center border border-white/15 text-bone transition-colors duration-300 hover:border-kred hover:text-kred xl:hidden"
            >
              {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
          </div>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.35 }}
            className="fixed inset-0 z-40 flex flex-col bg-[#070708]/97 backdrop-blur-2xl xl:hidden"
          >
            <div className="flex flex-1 flex-col justify-center overflow-y-auto px-8 pb-10 pt-32">
              <nav className="flex flex-col gap-1" aria-label="Mobile">
                {NAV_LINKS.map((l, i) => (
                  <motion.div
                    key={l.to}
                    initial={{ opacity: 0, x: -28 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.5, delay: 0.08 + i * 0.05, ease: [0.22, 1, 0.36, 1] }}
                  >
                    <NavLink
                      to={l.to}
                      data-testid={`mobile-nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                      className={({ isActive }) =>
                        `group flex items-baseline gap-4 border-b border-white/5 py-3 ${
                          isActive ? "text-kred" : "text-bone"
                        }`
                      }
                    >
                      <span className="font-mono text-[10px] tracking-[0.3em] text-gold/70">
                        {String(i + 1).padStart(2, "0")}
                      </span>
                      <span className="font-display text-4xl uppercase tracking-wide transition-transform duration-300 group-hover:translate-x-2">
                        {l.label}
                      </span>
                    </NavLink>
                  </motion.div>
                ))}
              </nav>
              <motion.div
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.6 }}
                className="mt-10"
              >
                <Link
                  to="/register"
                  data-testid="mobile-register-button"
                  className="clip-notch-sm inline-flex w-full items-center justify-center bg-kred px-8 py-5 font-mono text-xs font-bold uppercase tracking-[0.3em] text-white"
                >
                  Register
                </Link>
                <p className="mt-6 text-center font-mono text-[10px] uppercase tracking-[0.3em] text-slate-500">
                  Aotearoa • New Zealand
                </p>
                <div className="mt-5 flex items-center justify-center gap-3">
                  <a
                    href="https://www.instagram.com/kumitegames/"
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="Kumite Games on Instagram"
                    data-testid="mobile-social-instagram"
                    className="grid h-11 w-11 place-items-center border border-white/10 text-slate-400 transition-colors duration-300 hover:border-gold/60 hover:text-gold"
                  >
                    <Instagram className="h-4 w-4" />
                  </a>
                  <a
                    href="https://www.facebook.com/kumitegamesnz/"
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="Kumite Games on Facebook"
                    data-testid="mobile-social-facebook"
                    className="grid h-11 w-11 place-items-center border border-white/10 text-slate-400 transition-colors duration-300 hover:border-gold/60 hover:text-gold"
                  >
                    <Facebook className="h-4 w-4" />
                  </a>
                </div>
              </motion.div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
